import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Card, Col, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from '../contexts/LoadingContext';

const API = process.env.REACT_APP_API;

// Derive submission status from submitted_count / total_students
const getStatus = (row) => {
  const submitted = Number(row.submitted_count || 0);
  const total = Number(row.total_students || 0);
  if (submitted === 0) return 'none';        // ยังไม่ส่ง
  if (submitted < total) return 'partial';   // ส่งไม่ครบ
  return 'complete';                         // ครบ
};

const STATUS_META = {
  none:     { label: 'ยังไม่ส่ง', bg: 'danger'  },
  partial:  { label: 'ส่งไม่ครบ', bg: 'warning' },
  complete: { label: 'ครบ',       bg: 'success' },
};

const ALL_STATUSES = ['none', 'partial', 'complete'];

// การ์ดครูที่ยังส่งเกรดไม่ครบ 1 รายการ สำหรับมุมมองมือถือ
const TeacherPendingCard = ({ t }) => (
  <div className="border rounded p-3">
    <div className="fw-semibold">{t.t_name}</div>
    <div className="d-flex gap-3 mt-2 pt-2 border-top fs-sm">
      {t.none > 0 && <span>ยังไม่ส่ง <Badge bg="danger">{t.none}</Badge></span>}
      {t.partial > 0 && <span>ส่งไม่ครบ <Badge bg="warning">{t.partial}</Badge></span>}
    </div>
  </div>
)

// การ์ดรายวิชา 1 รายการ สำหรับมุมมองมือถือ
const SubjectSubmissionCard = ({ r }) => {
  const meta = STATUS_META[r.status];
  return (
    <div className="border rounded p-3">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="fw-semibold">
          <span className="text-muted">{r.sub_subid}</span> {r.sub_subname}
          {r.sub_type === 'extra' && <span className="text-muted"> (เพิ่มเติม)</span>}
        </div>
        <Badge bg={meta.bg} className="fs-xs">{meta.label}</Badge>
      </div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">ครูผู้สอน:</span> {r.t_name}</div>
        <div><span className="text-muted">ห้อง:</span> {r.class_room} · <span className="text-muted">ภาคเรียน:</span> {r.sub_semester}</div>
        <div><span className="text-muted">ส่งแล้ว:</span> {r.submitted_count}/{r.total_students}</div>
      </div>
    </div>
  )
}

export default function GradeSubmissionReport() {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const { setLoading } = useLoading();

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName('table');
    for (const t of tables) {
      const theads = t.querySelectorAll('thead');
      if (s === 'dark') {
        for (const th of theads) { th.classList.remove('table-secondary'); th.classList.add('table-primary'); }
        t.classList.add('table-dark');
      } else {
        t.classList.remove('table-dark');
        for (const th of theads) { th.classList.remove('table-primary'); th.classList.add('table-secondary'); }
      }
    }
  };
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId]     = useState(null);
  const [semester, setSemester] = useState('all');     // 'all' | '1' | '2'
  const [rows, setRows]         = useState([]);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES); // selected statuses to load
  const [teacherView, setTeacherView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');
  const [detailView, setDetailView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');

  const toggleStatus = (s) => {
    setStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  // Load study years, default to latest (list is ordered DESC)
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => {
        const years = res.data || [];
        setYearList(years);
        if (years.length > 0) setYearId(years[0].year_id);
      })
      .catch(() => {});
  }, []);

  // Fetch report on year / semester / status-filter change.
  // statusFilter is sent to the server so unchecked statuses are never loaded.
  useEffect(() => {
    if (!yearId) return;
    if (statusFilter.length === 0) { setRows([]); return; }
    setLoading(true);
    const sem = semester === 'all' ? '' : `/${semester}`;
    const statusParam = encodeURIComponent(statusFilter.join(','));
    axiosInstance.get(`${API}/getGradeSubmissionByYear/${yearId}${sem}?statuses=${statusParam}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => setRows(res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [yearId, semester, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach derived status
  const withStatus = useMemo(() => rows.map(r => ({ ...r, status: getStatus(r) })), [rows]);

  // KPI counts
  const kpi = useMemo(() => {
    const c = { total: withStatus.length, none: 0, partial: 0, complete: 0 };
    withStatus.forEach(r => { c[r.status] += 1; });
    return c;
  }, [withStatus]);

  // Per-teacher summary: count of assignments not fully submitted
  const teacherSummary = useMemo(() => {
    const map = new Map();
    withStatus.forEach(r => {
      if (!map.has(r.t_id)) map.set(r.t_id, { t_id: r.t_id, t_name: r.t_name, none: 0, partial: 0 });
      if (r.status === 'none') map.get(r.t_id).none += 1;
      else if (r.status === 'partial') map.get(r.t_id).partial += 1;
    });
    return [...map.values()]
      .filter(t => t.none + t.partial > 0)
      .sort((a, b) => (b.none + b.partial) - (a.none + a.partial));
  }, [withStatus]);

  // Server already filtered by status; show everything that was loaded.
  const detailRows = withStatus;

  const kpiCards = [
    { label: 'รายวิชาทั้งหมด', value: kpi.total,    color: 'primary', icon: 'ri-book-2-line' },
    { label: 'ส่งครบ',         value: kpi.complete, color: 'success', icon: 'ri-checkbox-circle-line' },
    { label: 'ส่งไม่ครบ',      value: kpi.partial,  color: 'warning', icon: 'ri-error-warning-line' },
    { label: 'ยังไม่ส่ง',      value: kpi.none,     color: 'danger',  icon: 'ri-close-circle-line' },
  ];

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">หัวหน้าวิชาการสามัญ</Link></li>
              <li className="breadcrumb-item active">รายงานการส่งเกรด</li>
            </ol>
            <h4 className="main-title mb-0">รายงานการส่งเกรด</h4>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            {/* Year filter */}
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 150 }}
              value={yearId || ''}
              onChange={e => setYearId(e.target.value ? Number(e.target.value) : null)}
            >
              {yearList.map(y => (
                <option key={y.year_id} value={y.year_id}>ปีการศึกษา {y.year_year}</option>
              ))}
            </select>

            {/* Semester filter */}
            <div className="btn-group btn-group-sm">
              {[['all','ทุกภาคเรียน'],['1','ภาคเรียน 1'],['2','ภาคเรียน 2']].map(([v, label]) => (
                <button
                  key={v}
                  className={`btn btn${semester === v ? '' : '-outline'}-primary`}
                  onClick={() => setSemester(v)}
                >{label}</button>
              ))}
            </div>

            {/* Status filter — drives server-side loading */}
            <div className="d-flex align-items-center gap-3 ms-1">
              {ALL_STATUSES.map(s => (
                <div key={s} className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`status-${s}`}
                    checked={statusFilter.includes(s)}
                    onChange={() => toggleStatus(s)}
                  />
                  <label className="form-check-label fs-sm" htmlFor={`status-${s}`}>
                    <Badge bg={STATUS_META[s].bg} className="fs-xs">{STATUS_META[s].label}</Badge>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <Row className="g-3 mb-4">
          {kpiCards.map(k => (
            <Col key={k.label} xs={6} xl={3}>
              <Card className={`card-one border-${k.color} border-start border-4`}>
                <Card.Body className="p-3">
                  <div className={`text-${k.color} mb-1`}><i className={`${k.icon} fs-40 lh-1`}></i></div>
                  <h6 className="fw-semibold text-dark mb-1">{k.label}</h6>
                  <h3 className={`fw-bold text-${k.color} mb-0`}>{k.value} <span className="fs-sm fw-normal text-muted">วิชา</span></h3>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-3">
          {/* Per-teacher summary */}
          <Col xl={4}>
            <Card className="card-one h-100">
              <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">ครูที่ยังส่งเกรดไม่ครบ</Card.Title>
                <Badge bg="danger" className="ms-1">{teacherSummary.length} คน</Badge>
                <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
                  <button type="button" className={`btn btn${teacherView === 'table' ? '' : '-outline'}-primary`}
                    onClick={() => setTeacherView('table')} title="มุมมองตาราง">
                    <i className="ri-table-line" />
                  </button>
                  <button type="button" className={`btn btn${teacherView === 'card' ? '' : '-outline'}-primary`}
                    onClick={() => setTeacherView('card')} title="มุมมองการ์ด">
                    <i className="ri-layout-grid-line" />
                  </button>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {teacherSummary.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="ri-checkbox-circle-line fs-3 text-success d-block mb-1"></i>
                    ครูทุกคนส่งเกรดครบแล้ว
                  </div>
                ) : teacherView === 'card' ? (
                  <div className="p-3" style={{ maxHeight: 520, overflowY: 'auto' }}>
                    <Row className="g-3">
                      {teacherSummary.map(t => (
                        <Col key={t.t_id} xs={12}>
                          <TeacherPendingCard t={t} />
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : (
                  <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                    <Table responsive hover size="sm" className="mb-0">
                      <thead className="table-secondary sticky-top">
                        <tr>
                          <th>ครูผู้สอน</th>
                          <th className="text-center" style={{ width: 90 }}>ยังไม่ส่ง</th>
                          <th className="text-center" style={{ width: 90 }}>ส่งไม่ครบ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teacherSummary.map(t => (
                          <tr key={t.t_id}>
                            <td className="fs-sm">{t.t_name}</td>
                            <td className="text-center">
                              {t.none > 0 ? <Badge bg="danger">{t.none}</Badge> : <span className="text-muted">-</span>}
                            </td>
                            <td className="text-center">
                              {t.partial > 0 ? <Badge bg="warning">{t.partial}</Badge> : <span className="text-muted">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Detail table */}
          <Col xl={8}>
            <Card className="card-one h-100">
              <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">รายวิชา</Card.Title>
                <Badge bg="secondary" className="ms-1">{detailRows.length} วิชา</Badge>
                <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
                  <button type="button" className={`btn btn${detailView === 'table' ? '' : '-outline'}-primary`}
                    onClick={() => setDetailView('table')} title="มุมมองตาราง">
                    <i className="ri-table-line" />
                  </button>
                  <button type="button" className={`btn btn${detailView === 'card' ? '' : '-outline'}-primary`}
                    onClick={() => setDetailView('card')} title="มุมมองการ์ด">
                    <i className="ri-layout-grid-line" />
                  </button>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {detailRows.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="ri-inbox-line fs-3 d-block mb-1"></i>
                    {statusFilter.length === 0 ? 'กรุณาเลือกสถานะอย่างน้อย 1 รายการ' : 'ไม่มีข้อมูลตามตัวกรอง'}
                  </div>
                ) : detailView === 'card' ? (
                  <div className="p-3" style={{ maxHeight: 520, overflowY: 'auto' }}>
                    <Row className="g-3">
                      {detailRows.map((r) => (
                        <Col key={r.teach_id} xs={12} md={6}>
                          <SubjectSubmissionCard r={r} />
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : (
                  <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                    <Table responsive hover size="sm" className="mb-0">
                      <thead className="table-secondary sticky-top">
                        <tr>
                          <th className="text-center" style={{ width: 50 }}>#</th>
                          <th>วิชา</th>
                          <th>ครูผู้สอน</th>
                          <th className="text-center" style={{ width: 70 }}>ห้อง</th>
                          <th className="text-center" style={{ width: 70 }}>ภาคเรียน</th>
                          <th className="text-center" style={{ width: 90 }}>ส่งแล้ว</th>
                          <th className="text-center" style={{ width: 100 }}>สถานะ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailRows.map((r, i) => {
                          const meta = STATUS_META[r.status];
                          return (
                            <tr key={r.teach_id}>
                              <td className="text-center">{i + 1}</td>
                              <td className="fs-sm">
                                <span className="text-muted">{r.sub_subid}</span> {r.sub_subname}
                                {r.sub_type === 'extra' && <span className="text-muted"> (เพิ่มเติม)</span>}
                              </td>
                              <td className="fs-sm">{r.t_name}</td>
                              <td className="text-center">{r.class_room}</td>
                              <td className="text-center">{r.sub_semester}</td>
                              <td className="text-center">{r.submitted_count}/{r.total_students}</td>
                              <td className="text-center"><Badge bg={meta.bg} className="fs-xs">{meta.label}</Badge></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
